import React, { useState, useEffect } from 'react'
import { useAuth } from '../../../shared/context/AuthContext'
import { useSearchParams } from 'react-router-dom'
import { InvitationRequests } from '../../invitations'
import AdminContactsManager from './AdminContactsManager'
import IsoConfigManager from '../../iso-configuration/components/IsoConfigManager'
import ISOManager from './ISOManager'
import { FieldMappingsManager } from '../../iso-application-field-mappings'
import '../styles/AdminDashboard.css'

interface User {
  id: string
  email: string
  name: string
  role: 'iso' | 'broker' | 'admin'
  status: 'active' | 'pending' | 'suspended' | 'rejected'
  lastLogin?: string
  createdAt: string
  updatedAt: string
}

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  
  // Get initial tab from URL params or default to 'users'
  const initialTab = (searchParams.get('tab') as 'users' | 'invitations' | 'isos' | 'contacts' | 'iso-config' | 'field-mappings') || 'users'
  const [activeTab, setActiveTab] = useState<'users' | 'invitations' | 'isos' | 'contacts' | 'iso-config' | 'field-mappings'>(initialTab)
  const [filter, setFilter] = useState({
    role: 'all',
    status: 'all',
    search: ''
  })

  const handleLogout = async () => {
    await logout()
  }

  const handleTabChange = (tab: 'users' | 'invitations' | 'isos' | 'contacts' | 'iso-config' | 'field-mappings') => {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/admin/users', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        setUsers(data.users || [])
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to fetch users')
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const filteredUsers = users.filter(u => {
    const matchesRole = filter.role === 'all' || u.role === filter.role
    const matchesStatus = filter.status === 'all' || u.status === filter.status
    const matchesSearch = filter.search === '' || 
      u.email.toLowerCase().includes(filter.search.toLowerCase()) ||
      u.name.toLowerCase().includes(filter.search.toLowerCase())
    
    return matchesRole && matchesStatus && matchesSearch
  })

  const getStatusBadge = (status: string) => {
    const badgeClass = {
      active: 'bg-success',
      pending: 'bg-warning',
      suspended: 'bg-danger',
      rejected: 'bg-secondary'
    }[status] || 'bg-secondary'

    return <span className={`badge ${badgeClass}`}>{status.toUpperCase()}</span>
  }

  const getRoleBadge = (role: string) => {
    const badgeClass = {
      admin: 'bg-primary',
      broker: 'bg-info',
      iso: 'bg-success'
    }[role] || 'bg-secondary'

    return <span className={`badge ${badgeClass}`}>{role.toUpperCase()}</span>
  }

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="admin-dashboard">
      {/* Left Sidebar Navigation */}
      <div className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-brand">
            <span className="admin-brand-icon">🛡️</span>
            <span className="admin-brand-text">Admin Portal</span>
          </div>
        </div>
        
        <nav className="admin-sidebar-nav">
          <ul className="admin-nav-list">
            <li className="admin-nav-item">
              <button 
                className={`admin-nav-button ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => handleTabChange('users')}
                type="button"
              >
                <i className="bi bi-people admin-nav-icon"></i>
                <span className="admin-nav-text">Users</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className={`admin-nav-button ${activeTab === 'invitations' ? 'active' : ''}`}
                onClick={() => handleTabChange('invitations')}
                type="button"
              >
                <i className="bi bi-envelope-plus admin-nav-icon"></i>
                <span className="admin-nav-text">Invitations</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className={`admin-nav-button ${activeTab === 'isos' ? 'active' : ''}`}
                onClick={() => handleTabChange('isos')}
                type="button"
              >
                <i className="bi bi-building admin-nav-icon"></i>
                <span className="admin-nav-text">ISOs</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className={`admin-nav-button ${activeTab === 'contacts' ? 'active' : ''}`}
                onClick={() => handleTabChange('contacts')}
                type="button"
              >
                <i className="bi bi-envelope-gear admin-nav-icon"></i>
                <span className="admin-nav-text">Notification Settings</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className={`admin-nav-button ${activeTab === 'iso-config' ? 'active' : ''}`}
                onClick={() => handleTabChange('iso-config')}
                type="button"
              >
                <i className="bi bi-gear-fill admin-nav-icon"></i>
                <span className="admin-nav-text">ISO Settings</span>
              </button>
            </li>
            <li className="admin-nav-item">
              <button 
                className={`admin-nav-button ${activeTab === 'field-mappings' ? 'active' : ''}`}
                onClick={() => handleTabChange('field-mappings')}
                type="button"
              >
                <i className="bi bi-diagram-3 admin-nav-icon"></i>
                <span className="admin-nav-text">Field Mappings</span>
              </button>
            </li>
          </ul>
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-info">
            <div className="admin-user-name">{user?.name}</div>
            <div className="admin-user-role">Administrator</div>
          </div>
          <button 
            className="admin-logout-btn"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-right me-2"></i>
            Logout
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="admin-main-content">
        <div className="admin-content-wrapper">

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="admin-content-section">
              {/* Page Header */}
              <div className="admin-content-header">
                <h2 className="admin-page-title">User Management</h2>
                <button 
                  className="btn btn-outline-primary"
                  onClick={fetchUsers}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Refreshing...
                    </>
                  ) : (
                    'Refresh'
                  )}
                </button>
              </div>

            {/* Page Description */}
            <div className="px-4 py-3 bg-light border-bottom">
              <p className="mb-0 text-muted">
                Manage system users, view their roles and status, and perform administrative actions. Monitor user activity and account management.
              </p>
            </div>

            {/* Filters */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Search</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name or email..."
                      value={filter.search}
                      onChange={(e) => setFilter({...filter, search: e.target.value})}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Role</label>
                    <select 
                      className="form-select"
                      value={filter.role}
                      onChange={(e) => setFilter({...filter, role: e.target.value})}
                    >
                      <option value="all">All Roles</option>
                      <option value="iso">ISO</option>
                      <option value="broker">Broker</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Status</label>
                    <select 
                      className="form-select"
                      value={filter.status}
                      onChange={(e) => setFilter({...filter, status: e.target.value})}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="suspended">Suspended</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Statistics</label>
                    <div className="d-flex gap-3">
                      <small className="text-muted">
                        Total: <strong>{users.length}</strong>
                      </small>
                      <small className="text-muted">
                        ISOs: <strong>{users.filter(u => u.role === 'iso').length}</strong>
                      </small>
                      <small className="text-muted">
                        Active: <strong>{users.filter(u => u.status === 'active').length}</strong>
                      </small>
                      <small className="text-muted">
                        Pending: <strong>{users.filter(u => u.status === 'pending').length}</strong>
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* Users Table */}
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading users...</p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-5">
                    <h5 className="text-muted">No users found</h5>
                    <p className="text-muted">
                      {filter.search || filter.role !== 'all' || filter.status !== 'all' 
                        ? 'Try adjusting your filters' 
                        : 'No users have been created yet'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Last Login</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((u) => (
                          <tr key={u.id}>
                            <td>
                              <strong>{u.name}</strong>
                            </td>
                            <td>{u.email}</td>
                            <td>{getRoleBadge(u.role)}</td>
                            <td>{getStatusBadge(u.status)}</td>
                            <td>
                              {u.lastLogin ? (
                                <small className="text-muted">
                                  {formatDate(u.lastLogin)}
                                </small>
                              ) : (
                                <small className="text-muted">Never</small>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {formatDate(u.createdAt)}
                              </small>
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm" role="group">
                                <button 
                                  className="btn btn-outline-primary"
                                  onClick={() => alert('View user details - Coming soon!')}
                                >
                                  View
                                </button>
                                <button 
                                  className="btn btn-outline-secondary"
                                  onClick={() => alert('Edit user - Coming soon!')}
                                >
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invitations Tab */}
        {activeTab === 'invitations' && (
          <div className="admin-content-section">
            <InvitationRequests />
          </div>
        )}

        {/* ISOs Tab */}
        {activeTab === 'isos' && (
          <div className="admin-content-section">
            <ISOManager />
          </div>
        )}

        {/* Contact Management Tab */}
        {activeTab === 'contacts' && (
          <div className="admin-content-section">
            <AdminContactsManager />
          </div>
        )}

        {/* ISO Account Configuration Tab */}
        {activeTab === 'iso-config' && (
          <div className="admin-content-section">
            <IsoConfigManager />
          </div>
        )}

        {/* Field Mappings Tab */}
        {activeTab === 'field-mappings' && (
          <div className="admin-content-section">
            <FieldMappingsManager />
          </div>
        )}
        
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard